import {
	Component,
	Input,
	OnDestroy,
	ViewChild,
	Signal,
	inject,
	computed,
	signal,
	output,
} from '@angular/core';
import { CommonModule } from '@angular/common';

import { TimingTowerService } from '../../timing-tower/timing-tower.service';

import { VbLxpVideoPlayerComponent } from '../video-player/vb-lxp-video-player.component';
import { RaceSessionService } from '../../timing-tower/race-session.service';
import { StreamData } from '../../shared/stream.token';
import { openVideoPopoutWindow } from './video-popout.util';
import { LivestreamViewService } from '../../livestream-view/livestream-view.service';

export type VideoSlotState = 'empty' | 'error' | 'playing';

@Component({
	selector: 'vb-lxp-video-slot',
	standalone: true,
	imports: [CommonModule, VbLxpVideoPlayerComponent],
	templateUrl: './video-slot.component.html',
	styleUrls: ['./video-slot.component.css'],
})
export class VbLxpVideoSlotComponent implements OnDestroy {
	private static readonly RESTORE_AUTOPLAY_MAX_ATTEMPTS = 40;
	private static readonly RESTORE_AUTOPLAY_RETRY_DELAY_MS = 150;

	public timingTowerService = inject(TimingTowerService);
	public raceSessionService = inject(RaceSessionService);
	public livestreamViewService = inject(LivestreamViewService);
	private readonly mvPlayerSignal = signal<VbLxpVideoPlayerComponent | undefined>(undefined);
	public readonly isPoppedOut = signal<boolean>(false);
	private popoutClosePollId: ReturnType<typeof setInterval> | null = null;
	private restoreAutoplayTimeoutId: ReturnType<typeof setTimeout> | null = null;

	@Input() label = '';
	@Input() time = '0:00:00';
	@Input() isLive = true;
	@Input() showBadge = false;
	@Input() progressPercent = 55;
	@Input() volumePercent = 70;
	@Input() src = '';

	public playerClosed = output<StreamData>();

	@ViewChild('mvPlayer')
	set mvPlayer(player: VbLxpVideoPlayerComponent | undefined) {
		this.mvPlayerSignal.set(player);
	}

	get mvPlayer(): VbLxpVideoPlayerComponent | undefined {
		return this.mvPlayerSignal();
	}

	public state: Signal<VideoSlotState> = computed(() => {
		const player = this.mvPlayerSignal();

		player?.getStreamDataVersion();
		if (player?.streamData) {
			return 'playing';
		} else {
			return 'empty';
		}
	});
	public onCloseClicked(event?: MouseEvent) {
		event?.stopPropagation();

		const streamData = this.mvPlayer?.streamData;

		if (!streamData) {
			this.isPoppedOut.set(false);
			return;
		}

		if (!streamData.carNumber) {
			this.livestreamViewService.isMainStreamSelected.set(false);
		} else {
			this.timingTowerService.setInactive(streamData.carNumber);
		}
		this.playerClosed.emit(streamData);
		this.mvPlayer.setStreamData(null);
		this.isPoppedOut.set(false);
		this.stopPopoutCloseMonitor();
		this.clearRestoreAutoplayTimeout();
	}

	public ngOnDestroy(): void {
		this.stopPopoutCloseMonitor();
		this.clearRestoreAutoplayTimeout();
	}

	public onPopoutClicked(event: MouseEvent) {
		event.stopPropagation();

		const player = this.mvPlayer;
		const streamData = player?.streamData;
		if (!streamData) {
			return;
		}

		if (streamData.id === 'twitch-stream') {
			const popup = window.open(
				'https://www.twitch.tv/sromotorsports',
				'_blank',
				'noopener,noreferrer',
			);
			if (popup) {
				const streamDataSnapshot = { ...streamData };
				const restoreToSlot = this.createPopoutRestoreHandler(streamDataSnapshot);
				popup.onbeforeunload = () => {
					restoreToSlot();

					return undefined;
				};
				this.mvPlayer?.setStreamData(null);
				this.isPoppedOut.set(true);
				this.startPopoutCloseMonitor(popup, restoreToSlot);
			}
			return;
		}

		const sourceUrl = player?.streamPlayerConfig?.sources?.[0]?.src;

		if (!sourceUrl) {
			return;
		}

		const title = streamData.teamName
			? `${streamData.teamName}${streamData.carNumber ? ` - #${streamData.carNumber}` : ''}`
			: streamData.id === 'main-stream'
				? 'Main Stream'
				: 'Stream';

		const streamDataSnapshot = { ...streamData };
		const restoreToSlot = this.createPopoutRestoreHandler(streamDataSnapshot);
		const popup = openVideoPopoutWindow({
			sourceUrl,
			title,
			isLive: player?.streamPlayerConfig?.isLive ?? streamData.isLive,
			onBeforeUnload: () => {
				restoreToSlot();
			},
		});

		if (popup) {
			this.mvPlayer?.setStreamData(null);
			this.isPoppedOut.set(true);
			this.startPopoutCloseMonitor(popup, restoreToSlot);
		}
	}

	private createPopoutRestoreHandler(streamDataSnapshot: StreamData): () => void {
		let restoreHandled = false;

		return () => {
			if (restoreHandled) {
				return;
			}

			restoreHandled = true;
			this.stopPopoutCloseMonitor();
			this.isPoppedOut.set(false);

			// Always rebind stream data so the original slot fully rehydrates after popout closes.
			this.mvPlayer?.setStreamData(streamDataSnapshot);

			if (streamDataSnapshot.id === 'twitch-stream') {
				this.mvPlayer?.setTwitchStream();
				return;
			}

			this.clearRestoreAutoplayTimeout();
			this.restoreAutoplayTimeoutId = setTimeout(() => {
				this.tryRestoreAutoplay();
			}, VbLxpVideoSlotComponent.RESTORE_AUTOPLAY_RETRY_DELAY_MS);
		};
	}

	private tryRestoreAutoplay(attempt: number = 0): void {
		const player = this.mvPlayer;
		const vgApi = player?.vgAPI;
		const expectedSource = player?.streamPlayerConfig?.sources?.[0]?.src;
		const activeSource = vgApi?.activeSource?.src;

		if (!player?.streamData || !vgApi || !expectedSource || activeSource !== expectedSource) {
			if (attempt >= VbLxpVideoSlotComponent.RESTORE_AUTOPLAY_MAX_ATTEMPTS) {
				return;
			}

			this.clearRestoreAutoplayTimeout();
			this.restoreAutoplayTimeoutId = setTimeout(() => {
				this.tryRestoreAutoplay(attempt + 1);
			}, VbLxpVideoSlotComponent.RESTORE_AUTOPLAY_RETRY_DELAY_MS);
			return;
		}

		this.clearRestoreAutoplayTimeout();
		vgApi.setVolume(0);

		Promise.resolve(vgApi.play())
			.catch(() => vgApi.play())
			.catch(() => {
				if (attempt >= VbLxpVideoSlotComponent.RESTORE_AUTOPLAY_MAX_ATTEMPTS) {
					return;
				}

				this.clearRestoreAutoplayTimeout();
				this.restoreAutoplayTimeoutId = setTimeout(() => {
					this.tryRestoreAutoplay(attempt + 1);
				}, VbLxpVideoSlotComponent.RESTORE_AUTOPLAY_RETRY_DELAY_MS);
			});
	}

	private startPopoutCloseMonitor(popup: Window, onClosed: () => void): void {
		this.stopPopoutCloseMonitor();

		this.popoutClosePollId = setInterval(() => {
			if (!popup.closed) {
				return;
			}

			this.stopPopoutCloseMonitor();
			onClosed();
		}, 500);
	}

	private stopPopoutCloseMonitor(): void {
		if (!this.popoutClosePollId) {
			return;
		}

		clearInterval(this.popoutClosePollId);
		this.popoutClosePollId = null;
	}

	private clearRestoreAutoplayTimeout(): void {
		if (!this.restoreAutoplayTimeoutId) {
			return;
		}

		clearTimeout(this.restoreAutoplayTimeoutId);
		this.restoreAutoplayTimeoutId = null;
	}
}
